/**
 * AddressManager — Full screen address management view
 * Opens when user taps "Add address / Change" on checkout
 * Has its own back button → returns to main checkout
 * Views: LIST → FORM (add/edit)
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  ArrowLeftOutlined,
  EllipsisOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Popconfirm,
  Radio,
  Row,
  Skeleton,
  Space,
  Tag,
  App,
} from "antd";
import AddressForm from "./AddressForm";
import { addressAPI } from "../../api/services";

const ADDRESS_TYPE_LABELS = {
  home:    "Home",
  work:    "Work",
  other:   "Other",
  friends: "Friends / Family",
};

const VIEW = { LIST: "list", FORM: "form" };

const AddressManager = ({
  selectedAddress,
  onSelectAddress,  // (addr) => void — called when user picks an address
  onBack,           // () => void — return to main checkout
  setHeaderTitle,
}) => {
  const { notification } = App.useApp();
  const [addresses, setAddresses]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState(VIEW.LIST);
  const [editingAddr, setEditingAddr] = useState(null);

  // ─── Fetch ─────────────────────────────────────────────────────────────────
  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const res    = await addressAPI.getAll();
      const raw    = res?.data?.data ?? res?.data ?? res ?? [];
      const active = (Array.isArray(raw) ? raw : []).filter((a) => a?.status === "active");
      setAddresses(active);
      return active;
    } catch (err) {
      notification.error({ message: err?.message || "Failed to load addresses" });
      return [];
    } finally {
      setLoading(false);
    }
  }, [notification]);

  useEffect(() => { fetchAddresses(); }, []);

  // ─── Header sync ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!setHeaderTitle) return;
    if (view === VIEW.FORM) {
      setHeaderTitle(
        <Header
          title={editingAddr?.uuid ? "Edit Address" : "Add New Address"}
          onBack={() => { setView(VIEW.LIST); setEditingAddr(null); }}
        />
      );
    } else {
      setHeaderTitle(
        <Header title="Select Delivery Address" onBack={onBack} />
      );
    }
  }, [view, editingAddr, setHeaderTitle, onBack]);

  // Cleanup header on unmount
  useEffect(() => () => setHeaderTitle?.(null), []);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (addr) => {
    try {
      await addressAPI.delete(addr.uuid);
      notification.success({ message: "Address deleted!" });
      const updated = await fetchAddresses();
      if (selectedAddress?.uuid === addr.uuid) {
        onSelectAddress(updated.length > 0 ? updated[0] : null);
      }
    } catch (err) {
      notification.error({ message: err?.message || "Failed to delete address" });
    }
  }, [selectedAddress, fetchAddresses, onSelectAddress, notification]);

  const handleFormClose = useCallback(async () => {
    const updated = await fetchAddresses();
    // Auto-select first if nothing selected
    if (!selectedAddress && updated.length > 0) onSelectAddress(updated[0]);
    setView(VIEW.LIST);
    setEditingAddr(null);
  }, [fetchAddresses, selectedAddress, onSelectAddress]);

  const handleSelect = (addr) => {
    onSelectAddress(addr);
    onBack(); // go back to checkout after selecting
  };

  // ─── FORM ────────────────────────────────────────────────────────────────────
  if (view === VIEW.FORM) {
    return (
      <div style={{ padding: "8px 0 24px" }}>
        <AddressForm
          isAddressForm={editingAddr}
          onClose={handleFormClose}
        />
      </div>
    );
  }

  // ─── LIST ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "8px 0 24px" }}>
      {loading ? (
        <Skeleton active title={false} paragraph={{ rows: 4 }} />
      ) : (
        <>
          {addresses.length === 0 ? (
            <div style={emptyState}>
              <div style={{ fontSize: 14, color: "#bbb", marginBottom: 4 }}>
                No saved addresses
              </div>
              <div style={{ fontSize: 12, color: "#ccc" }}>
                Add a new address to continue
              </div>
            </div>
          ) : (
            <Card style={listCard} bodyStyle={{ padding: 0 }}>
              {addresses.map((addr, idx) => (
                <div key={addr.uuid}>
                  {idx > 0 && <div style={divider} />}
                  <div style={{ padding: "14px 16px" }}>
                    <Row justify="space-between" wrap={false} align="top">

                      {/* Radio + Details */}
                      <Col flex={1}>
                        <Row align="top" wrap={false} gutter={10}>
                          <Col>
                            <Radio
                              checked={selectedAddress?.uuid === addr.uuid}
                              onChange={() => handleSelect(addr)}
                              style={{ marginTop: 3 }}
                            />
                          </Col>
                          <Col
                            flex={1}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleSelect(addr)}
                          >
                            <Row align="middle" gutter={6} wrap={false}>
                              <Col>
                                <span style={{ fontWeight: 600, fontSize: 14 }}>
                                  {addr.full_name}
                                </span>
                              </Col>
                              <Col>
                                <Tag style={tagStyle}>
                                  {ADDRESS_TYPE_LABELS[addr.address_type] || addr.address_type}
                                </Tag>
                              </Col>
                            </Row>
                            <div style={{ fontSize: 12, color: "#555", marginTop: 3, lineHeight: 1.5 }}>
                              {addr.address_line1}, {addr.city}, {addr.state}
                            </div>
                            <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                              +91 {addr.phone} &nbsp;·&nbsp; {addr.email}
                            </div>
                          </Col>
                        </Row>
                      </Col>

                      {/* 3-dot menu */}
                      <Col>
                        <Dropdown
                          trigger={["click"]}
                          menu={{
                            items: [
                              {
                                key: "edit",
                                label: "Edit",
                                onClick: () => {
                                  setEditingAddr(addr);
                                  setView(VIEW.FORM);
                                },
                              },
                              {
                                key: "delete",
                                label: (
                                  <Popconfirm
                                    title="Delete this address?"
                                    okText="Yes"
                                    cancelText="No"
                                    placement="topRight"
                                    onConfirm={() => handleDelete(addr)}
                                  >
                                    <span style={{ color: "#ff4d4f" }}>Delete</span>
                                  </Popconfirm>
                                ),
                              },
                            ],
                          }}
                        >
                          <EllipsisOutlined
                            style={{
                              fontSize: 20,
                              cursor: "pointer",
                              padding: "2px 6px",
                              color: "#bbb",
                            }}
                          />
                        </Dropdown>
                      </Col>
                    </Row>
                  </div>
                </div>
              ))}
            </Card>
          )}

          {/* Add New Address */}
          <Button
            block
            icon={<PlusOutlined />}
            onClick={() => { setEditingAddr({}); setView(VIEW.FORM); }}
            style={addBtn}
          >
            Add New Address
          </Button>
        </>
      )}
    </div>
  );
};

// ─── Header with back button ───────────────────────────────────────────────────
const Header = ({ title, onBack }) => (
  <Row align="middle" gutter={12}>
    <Col>
      <ArrowLeftOutlined
        style={{ fontSize: 18, cursor: "pointer", color: "#333" }}
        onClick={onBack}
      />
    </Col>
    <Col>
      <span style={{ fontSize: 16, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>
        {title}
      </span>
    </Col>
  </Row>
);

// ─── Styles ────────────────────────────────────────────────────────────────────
const listCard = {
  borderRadius: 10,
  border: "1px solid #e8e8e8",
  overflow: "hidden",
  boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
  marginBottom: 12,
};

const divider = {
  height: 1,
  backgroundColor: "#f0f0f0",
  margin: "0 16px",
};

const tagStyle = {
  fontSize: 10,
  borderRadius: 20,
  border: "1px solid #eee",
  backgroundColor: "#f5f5f5",
  color: "#666",
  padding: "0 8px",
};

const addBtn = {
  height: 46,
  borderRadius: 8,
  border: "1px solid #ddd",
  fontSize: 14,
  fontWeight: 500,
  color: "#333",
  backgroundColor: "#f9f9f9",
  marginTop: 4,
};

const emptyState = {
  textAlign: "center",
  padding: "32px 16px",
  backgroundColor: "#fafafa",
  borderRadius: 10,
  border: "1px solid #f0f0f0",
  marginBottom: 12,
};

export default AddressManager;